import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import QRCode from 'react-qr-code';
import WalletLedgerDialog from '../components/WalletLedgerDialog';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserAvatar } from '../components/UserAvatar';
import {
  LayoutDashboard, Users, Wallet, History, Copy, Check, X,
  ArrowUpCircle, ArrowDownCircle, Upload, Eye, Building2, Trophy, BarChart3, IndianRupee, Zap, FileBarChart
} from 'lucide-react';

function StatusBadge({ status }) {
  const cls = status === 'pending' ? 'badge-pending' : status === 'approved' ? 'badge-approved' : 'badge-rejected';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>{status}</span>;
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.05)' }}>
      {title && (
        <h3 className="flex items-center gap-2 text-base font-bold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
          {Icon && <Icon size={16} style={{ color: 'var(--neon-primary)' }} />} {title}
        </h3>
      )}
      {children}
    </div>
  );
}

function ApproveRejectDialog({ open, onClose, onSubmit, title }) {
  const [msg, setMsg] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader><DialogTitle style={{ color: 'var(--neon-primary)', fontFamily: 'var(--font-heading)' }}>{title}</DialogTitle></DialogHeader>
        <textarea data-testid="sa-message-input" className="neon-input w-full h-20 p-3 rounded-lg resize-none text-sm" placeholder="Message to user"
          value={msg} onChange={(e) => setMsg(e.target.value)}
          style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }} />
        <div className="flex gap-3">
          <button data-testid="sa-dialog-approve-btn" onClick={() => { onSubmit('approve', msg); onClose(); setMsg(''); }}
            className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'rgba(1,255,195,0.15)', color: '#01FFC3' }}>Approve</button>
          <button data-testid="sa-dialog-reject-btn" onClick={() => { if (!msg) { toast.error('Message required for reject'); return; } onSubmit('reject', msg); onClose(); setMsg(''); }}
            className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>Reject</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Dashboard =====
function SADashboard() {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(() => {
    api.get('/subadmin/dashboard').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (!stats) return <p style={{ color: 'var(--neon-text-muted)' }}>Loading...</p>;

  const copyCode = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const referralLink = `${window.location.origin}/login?ref=${stats.referral_code}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const referralLink = `${window.location.origin}/login?ref=${stats.referral_code}`;
    const message = `Join Mukamari using my link and get bonus!\n${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Referral Link Banner */}
      <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(5,217,232,0.15), rgba(255,42,109,0.1))', border: '1px solid rgba(5,217,232,0.3)' }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>Your Referral Code</p>
            <p className="text-2xl font-black" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>{stats.referral_code}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="sa-copy-referral" onClick={copyCode}
              className="p-3 rounded-xl transition-all hover:scale-110" style={{ background: 'var(--neon-surface)', color: 'var(--neon-primary)' }}>
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
            <button data-testid="sa-share-whatsapp" onClick={shareWhatsApp}
              className="p-3 rounded-xl transition-all hover:scale-110" style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
          </div>
        </div>
        <p className="text-xs" style={{ color: 'var(--neon-text-secondary)' }}>
          Share this link — users will auto-join under you!
        </p>
        <p className="text-[10px] mt-1 break-all" style={{ color: 'var(--neon-text-muted)' }}>
          {window.location.origin}/login?ref={stats.referral_code}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'My Users', value: stats.total_users, color: 'var(--neon-primary)' },
          { label: 'My Wallet', value: stats.wallet_balance, color: 'var(--neon-accent)' },
          { label: 'Pending Deposits', value: stats.pending_deposits, color: '#01FFC3' },
          { label: 'Pending Withdrawals', value: stats.pending_withdrawals, color: 'var(--neon-secondary)' },
          { label: "Today's Bets", value: stats.today_bet_amount, color: 'var(--neon-primary)' },
        ].map((it, i) => (
          <div key={i} data-testid={`sa-stat-${i}`} className="p-4 rounded-xl" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>{it.label}</p>
            <p className="text-2xl font-black mt-1" style={{ fontFamily: 'var(--font-heading)', color: it.color }}>{it.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Users =====
function SAUsers() {
  const [users, setUsers] = useState([]);
  const [ledgerUserId, setLedgerUserId] = useState(null);
  const fetchUsers = useCallback(() => {
    api.get('/subadmin/users').then(r => setUsers((r.data.users || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' })))).catch(() => {});
  }, []);

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(fetchUsers, 5000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  return (
    <SectionCard title={`My Users (${users.length})`} icon={Users}>
      <WalletLedgerDialog open={!!ledgerUserId} onClose={() => setLedgerUserId(null)} userId={ledgerUserId} endpoint="/subadmin/user-ledger" />
      {users.length === 0 && <p className="text-sm py-4 text-center" style={{ color: 'var(--neon-text-muted)' }}>No users yet. Share your referral code to get users!</p>}
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} data-testid={`sa-user-${u.id}`} className="p-3 rounded-lg flex items-center justify-between" style={{ background: 'var(--neon-surface)' }}>
            <div className="flex items-center gap-3">
              <UserAvatar name={u.name} size={36} />
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{u.name}</p>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{u.email} {u.mobile ? `| ${u.mobile}` : ''}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{u.wallet_balance || 0}</p>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Bets: {u.total_bets || 0}</p>
              </div>
              <button
                data-testid={`sa-user-ledger-${u.id}`}
                onClick={() => setLedgerUserId(u.id)}
                title="Wallet History"
                className="p-1.5 rounded-lg"
                style={{ background: 'rgba(5,217,232,0.12)', color: 'var(--neon-primary)' }}
              >
                <FileBarChart size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ===== User Wallet Requests =====
function SAWalletRequests() {
  const [reqs, setReqs] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });

  const fetchReqs = useCallback(() => {
    api.get('/subadmin/wallet-requests').then(r => setReqs(r.data.requests)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchReqs();
    const interval = setInterval(fetchReqs, 5000);
    return () => clearInterval(interval);
  }, [fetchReqs]);

  const handle = async (action, msg) => {
    try {
      await api.put(`/subadmin/wallet-requests/${dialog.id}`, { action, admin_message: msg });
      toast.success(`${action}d`);
      fetchReqs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const getScreenshotUrl = (path) => {
    if (!path) return null;
    const token = localStorage.getItem('neonplay_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  return (
    <div className="space-y-3">
      <ApproveRejectDialog open={dialog.open} onClose={() => setDialog({ open: false, id: null })} onSubmit={handle} title="User Request Action" />
      {reqs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No requests</p>}
      {reqs.map(r => (
        <SectionCard key={r.id}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.user_name}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{r.user_email}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg" style={{ color: r.type === 'add' ? 'var(--neon-primary)' : 'var(--neon-secondary)' }}>
                {r.type === 'add' ? '+' : '-'}{r.amount}
              </p>
              <p className="text-xs uppercase font-bold" style={{ color: 'var(--neon-text-muted)' }}>{r.type}</p>
            </div>
          </div>
          {r.utr_number && <p className="text-xs mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UTR: <strong>{r.utr_number}</strong></p>}
          {r.screenshot_path && (
            <div className="mb-2">
              <img src={getScreenshotUrl(r.screenshot_path)} alt="Screenshot" className="max-h-32 rounded-lg border" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            </div>
          )}
          {r.type === 'withdraw' && (r.user_upi || r.user_account) && (
            <div className="p-3 rounded-lg mb-2 text-xs" style={{ background: 'var(--neon-bg)' }}>
              {r.user_upi && (
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UPI ID:</p>
                    <p className="font-bold break-all mb-2" style={{ color: 'var(--neon-accent)' }}>{r.user_upi}</p>
                    <button
                      data-testid={`sa-copy-upi-${r.id}`}
                      onClick={() => { navigator.clipboard.writeText(r.user_upi); toast.success('UPI ID copied'); }}
                      className="text-[10px] px-2 py-1 rounded font-bold"
                      style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}
                    >
                      <Copy size={10} className="inline mr-1" />Copy UPI
                    </button>
                  </div>
                  <div className="shrink-0 p-2 rounded-lg bg-white" data-testid={`sa-qr-upi-${r.id}`}>
                    <QRCode
                      value={`upi://pay?pa=${encodeURIComponent(r.user_upi)}&pn=${encodeURIComponent(r.user_name || 'User')}&am=${r.amount}&cu=INR&tn=Mukamari%20Withdrawal`}
                      size={96}
                      level="M"
                    />
                    <p className="text-[9px] text-center mt-1 font-bold" style={{ color: '#000' }}>Scan & Pay ₹{r.amount}</p>
                  </div>
                </div>
              )}
              {r.user_account && <p style={{ color: 'var(--neon-text-secondary)' }}>Acc: <strong>{r.user_account}</strong> | IFSC: <strong>{r.user_ifsc}</strong></p>}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{new Date(r.created_at).toLocaleString()}</span>
            </div>
            {r.status === 'pending' && (
              <button data-testid={`sa-action-wallet-${r.id}`} onClick={() => setDialog({ open: true, id: r.id })}
                className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
                Action
              </button>
            )}
          </div>
          {r.admin_message && <p className="mt-2 text-xs p-2 rounded" style={{ background: 'var(--neon-bg)', color: 'var(--neon-text-secondary)' }}>Message: {r.admin_message}</p>}
        </SectionCard>
      ))}
    </div>
  );
}

// ===== My Wallet (Sub-Admin requesting from Main Admin) =====
function SAMyWallet() {
  const [wallet, setWallet] = useState(null);
  const [form, setForm] = useState({ amount: '', type: 'add', note: '', utr_number: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchWallet = useCallback(() => {
    api.get('/subadmin/wallet').then(r => setWallet(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchWallet();
    const interval = setInterval(fetchWallet, 5000);
    return () => clearInterval(interval);
  }, [fetchWallet]);

  const uploadScreenshot = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/screenshot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setScreenshot(res.data.path);
      toast.success('Screenshot uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return; }
    if (form.type === 'add' && !form.utr_number) { toast.error('UTR number required'); return; }
    setLoading(true);
    try {
      await api.post('/subadmin/wallet/request', {
        amount: parseFloat(form.amount), type: form.type,
        note: form.note, utr_number: form.utr_number,
        screenshot_path: screenshot
      });
      toast.success('Request submitted to Main Admin');
      setForm({ amount: '', type: 'add', note: '', utr_number: '' });
      setScreenshot(null);
      fetchWallet();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) return <p style={{ color: 'var(--neon-text-muted)' }}>Loading...</p>;

  return (
    <div className="space-y-4">
      {/* Balance Card */}
      <div className="p-5 rounded-xl text-center" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(5,217,232,0.1))', border: '1px solid rgba(255,215,0,0.2)' }}>
        <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>My Wallet Balance</p>
        <p className="text-4xl font-black mt-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)' }}>{wallet.balance}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--neon-text-secondary)' }}>Request deposit from Main Admin to fund your wallet</p>
      </div>

      {/* Request Form */}
      <SectionCard title="Request Deposit / Withdrawal" icon={Wallet}>
        <div className="flex gap-2 mb-4">
          {['add', 'withdraw'].map(t => (
            <button key={t} data-testid={`sa-wallet-type-${t}`}
              className={`flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${form.type === t ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
              onClick={() => setForm(f => ({ ...f, type: t }))}>
              {t === 'add' ? <><ArrowUpCircle size={16} /> Deposit</> : <><ArrowDownCircle size={16} /> Withdraw</>}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Amount</Label>
            <Input data-testid="sa-wallet-amount" className="neon-input" type="number" placeholder="Enter amount"
              value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
          {form.type === 'add' && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>UTR Number</Label>
                <Input data-testid="sa-wallet-utr" className="neon-input" placeholder="Enter UTR number"
                  value={form.utr_number} onChange={(e) => setForm(f => ({ ...f, utr_number: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Screenshot</Label>
                <label className="flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm"
                  style={{ background: 'var(--neon-surface)', border: '1px dashed rgba(255,255,255,0.2)', color: 'var(--neon-text-secondary)' }}>
                  <Upload size={14} />
                  {uploading ? 'Uploading...' : screenshot ? 'Screenshot uploaded' : 'Upload screenshot'}
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadScreenshot(e.target.files[0])} />
                </label>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Note (optional)</Label>
            <Input data-testid="sa-wallet-note" className="neon-input" placeholder="Any note"
              value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} />
          </div>
          <button data-testid="sa-wallet-submit" onClick={submit} disabled={loading}
            className="w-full btn-neon-primary disabled:opacity-50">
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </SectionCard>

      {/* Transaction History */}
      <SectionCard title="My Transactions" icon={History}>
        {wallet.transactions.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No transactions yet</p>}
        {wallet.transactions.map((tx, i) => (
          <div key={tx.id || i} className="p-3 rounded-lg mb-2 flex items-center justify-between" style={{ background: 'var(--neon-surface)' }}>
            <div>
              <p className="font-bold text-sm" style={{ color: tx.type === 'add' ? '#01FFC3' : 'var(--neon-secondary)' }}>
                {tx.type === 'add' ? '+' : '-'}{tx.amount}
              </p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{new Date(tx.created_at).toLocaleString()}</p>
            </div>
            <StatusBadge status={tx.status} />
          </div>
        ))}
      </SectionCard>
    </div>
  );
}


// ===== SA UPI Manager =====
function SAUpiManager() {
  const [upis, setUpis] = useState([]);
  const [newUpi, setNewUpi] = useState('');

  useEffect(() => {
    api.get('/subadmin/upis').then(r => setUpis(r.data.upis || [])).catch(() => {});
  }, []);

  const addUpi = async () => {
    if (!newUpi.trim()) { toast.error('Enter UPI ID'); return; }
    try {
      const res = await api.post('/subadmin/upis', { upi_id: newUpi.trim() });
      setUpis(res.data.upis);
      setNewUpi('');
      toast.success('UPI added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteUpi = async (idx) => {
    try {
      const res = await api.delete(`/subadmin/upis/${idx}`);
      setUpis(res.data.upis);
      toast.success('UPI removed');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-2">
      {upis.map((u, i) => (
        <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
          <span className="text-sm font-bold break-all" style={{ color: 'var(--neon-accent)' }}>{u}</span>
          <button onClick={() => deleteUpi(i)} className="p-1 rounded" style={{ color: '#FF2A6D' }}><X size={14} /></button>
        </div>
      ))}
      {upis.length < 20 && (
        <div className="flex gap-2">
          <Input data-testid="sa-new-upi-input" className="neon-input flex-1" placeholder="your-upi@bank"
            value={newUpi} onChange={e => setNewUpi(e.target.value)} />
          <button data-testid="sa-add-upi-btn" onClick={addUpi} className="btn-neon-primary text-sm px-4">Add</button>
        </div>
      )}
      <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>{upis.length}/20 UPI IDs</p>
    </div>
  );
}

// ===== Profile =====
function SAProfile() {
  const { user } = useAuth();
  const [autoBetEnabled, setAutoBetEnabled] = useState(false);
  const [autoBetMinAmount, setAutoBetMinAmount] = useState(50);

  useEffect(() => {
    api.get('/subadmin/auto-bet-settings').then(r => {
      setAutoBetEnabled(r.data.auto_bet_enabled || false);
      setAutoBetMinAmount(r.data.auto_bet_min_amount || 50);
    }).catch(() => {});
  }, []);

  const toggleAutoBet = async () => {
    try {
      const newVal = !autoBetEnabled;
      await api.put('/subadmin/auto-bet-settings', { auto_bet_enabled: newVal, auto_bet_min_amount: autoBetMinAmount });
      setAutoBetEnabled(newVal);
      toast.success(newVal ? 'Auto-Bet ON' : 'Auto-Bet OFF');
    } catch { toast.error('Failed'); }
  };

  const saveAutoBetAmount = async () => {
    try {
      await api.put('/subadmin/auto-bet-settings', { auto_bet_min_amount: autoBetMinAmount, auto_bet_enabled: autoBetEnabled });
      toast.success('Min amount updated');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="My Profile" icon={Users}>
        <div className="space-y-3">
          <div className="flex items-center gap-4 mb-4">
            <UserAvatar name={user?.name || 'S'} size={56} />
            <div>
              <p className="font-bold text-lg" style={{ color: 'var(--neon-text)' }}>{user?.name}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{user?.email}</p>
              <p className="text-xs font-bold px-2 py-0.5 rounded mt-1 inline-block" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>Sub-Admin</p>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="My UPI IDs (for users)" icon={Wallet}>
        <p className="text-xs mb-3" style={{ color: 'var(--neon-text-muted)' }}>
          Add multiple UPI IDs (max 20). Users will see them rotated automatically.
        </p>
        <SAUpiManager />
      </SectionCard>

      {/* Auto-Bet Settings */}
      <SectionCard title="Auto-Bet (Forward Betting)" icon={Zap}>
        <p className="text-xs mb-3" style={{ color: 'var(--neon-text-muted)' }}>
          ON करने पर, आपके users की bets automatically आपकी ID से भी लग जाएंगी (mirror bet)। आप minimum amount set कर सकते हैं।
        </p>
        <div className="flex items-center justify-between p-3 rounded-lg mb-3"
          style={{ background: autoBetEnabled ? 'rgba(1,255,195,0.1)' : 'var(--neon-surface)', border: `1px solid ${autoBetEnabled ? 'rgba(1,255,195,0.3)' : 'rgba(255,255,255,0.05)'}` }}>
          <div>
            <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>Auto-Bet Mode</p>
            <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
              {autoBetEnabled ? 'Users की bets auto-forward हो रही हैं' : 'बंद है — manual betting only'}
            </p>
          </div>
          <button data-testid="sa-auto-bet-toggle" onClick={toggleAutoBet}
            className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
            style={{ background: autoBetEnabled ? 'rgba(1,255,195,0.2)' : 'rgba(255,42,109,0.15)', color: autoBetEnabled ? '#01FFC3' : '#FF2A6D' }}>
            {autoBetEnabled ? 'ON' : 'OFF'}
          </button>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Min Amount (₹ से ऊपर forward)</Label>
            <Input data-testid="sa-auto-bet-min" className="neon-input" type="number" min={0}
              value={autoBetMinAmount} onChange={e => setAutoBetMinAmount(parseInt(e.target.value) || 0)} />
          </div>
          <button data-testid="sa-auto-bet-save" onClick={saveAutoBetAmount}
            className="btn-neon-primary text-sm px-4 py-2">Save</button>
        </div>
        <p className="text-[10px] mt-2" style={{ color: 'var(--neon-text-muted)' }}>
          Example: Min ₹50 → User ₹100 bet → आपकी ID से ₹100 auto-bet। ₹30 bet → forward नहीं होगी।
        </p>
      </SectionCard>
    </div>
  );
}

// ===== Bet History =====
function SABets() {
  const [bets, setBets] = useState([]);
  useEffect(() => {
    api.get('/subadmin/bets').then(r => setBets(r.data.bets)).catch(() => {});
  }, []);

  return (
    <SectionCard title="User Bet History" icon={History}>
      {bets.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No bets yet</p>}
      <div className="space-y-2">
        {bets.map((b, i) => (
          <div key={i} className="p-3 rounded-lg flex items-center justify-between" style={{ background: 'var(--neon-surface)' }}>
            <div>
              <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>
                {b.user_name} - #{b.number} ({b.option_text})
              </p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{b.company_name} | {new Date(b.created_at).toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="font-bold" style={{ color: 'var(--neon-accent)' }}>{b.amount}</p>
              <StatusBadge status={b.status} />
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ===== Sub-Admin Companies =====
function SACompanies() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: '', short_name: '', result_time: '', bet_open_time: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ result_time: '', timer_minutes: 60, bet_open_time: '' });

  const fetch = useCallback(() => {
    api.get('/subadmin/companies').then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const addCompany = async () => {
    if (!form.name.trim()) { toast.error('Company name required'); return; }
    if (!form.result_time.trim()) { toast.error('Result time required'); return; }
    try {
      await api.post('/subadmin/companies', form);
      toast.success('Company added!');
      setForm({ name: '', short_name: '', result_time: '', bet_open_time: '' });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveEdit = async (cid) => {
    try {
      await api.put(`/subadmin/companies/${cid}`, editForm);
      toast.success('Updated'); setEditingId(null); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteCompany = async (cid) => {
    if (!window.confirm('Delete this company?')) return;
    try {
      await api.delete(`/subadmin/companies/${cid}`);
      toast.success('Deleted'); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const calcClose = (rt) => {
    if (!rt || !/^\d{2}:\d{2}$/.test(rt)) return '';
    const [h, m] = rt.split(':').map(Number);
    let t = h * 60 + m - 10;
    if (t < 0) t += 1440;
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  };

  return (
    <SectionCard title="My Companies" icon={Building2}>
      {/* Add Form */}
      <div className="mb-4 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <Input data-testid="sa-company-name" className="neon-input" placeholder="Company Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input data-testid="sa-company-short" className="neon-input" placeholder="Short Name" value={form.short_name} onChange={e => setForm(f => ({ ...f, short_name: e.target.value }))} />
          <Input data-testid="sa-company-result-time" className="neon-input" placeholder="Result Time (HH:MM)" value={form.result_time} onChange={e => setForm(f => ({ ...f, result_time: e.target.value }))} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Bet Open Time</Label>
            <input data-testid="sa-company-bet-open-time" type="time" className="w-full p-2 rounded-lg text-sm"
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={form.bet_open_time} onChange={e => setForm(f => ({ ...f, bet_open_time: e.target.value }))} />
          </div>
        </div>
        {form.result_time && calcClose(form.result_time) && (
          <p className="text-xs mt-1" style={{ color: 'var(--neon-accent)' }}>
            Closing Time (auto): {calcClose(form.result_time)}
            {form.bet_open_time && <span> | Opens: {form.bet_open_time}</span>}
          </p>
        )}
        <button data-testid="sa-add-company-btn" onClick={addCompany} className="btn-neon-primary text-sm w-full">Add Company</button>
      </div>

      {/* List */}
      {companies.length === 0 ? (
        <p className="text-sm text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>No companies yet</p>
      ) : (
        <div className="space-y-2">
          {companies.map(c => (
            <div key={c.id} className="glass-card rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>
                    {c.name} <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>({c.short_name})</span>
                    {c.status === 'pending_approval' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>Pending Approval</span>
                    )}
                    {c.status === 'rejected' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>Rejected</span>
                    )}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-secondary)' }}>Result: {c.result_time || '—'} | Close: {c.closing_time || '—'}{c.bet_open_time ? ` | Opens: ${c.bet_open_time}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`sa-edit-company-${c.id}`} onClick={() => { setEditingId(editingId === c.id ? null : c.id); setEditForm({ result_time: c.result_time || '', timer_minutes: c.timer_minutes || 60, bet_open_time: c.bet_open_time || '' }); }}
                    className="text-xs px-2 py-1 rounded" style={{ background: 'var(--neon-surface)', color: 'var(--neon-accent)' }}>Edit</button>
                  <button data-testid={`sa-del-company-${c.id}`} onClick={() => deleteCompany(c.id)}
                    className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(255,42,109,0.2)', color: '#FF2A6D' }}>Delete</button>
                </div>
              </div>
              {editingId === c.id && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Result Time</Label>
                    <Input className="neon-input text-sm" placeholder="HH:MM" value={editForm.result_time} onChange={e => setEditForm(f => ({ ...f, result_time: e.target.value }))} />
                    {editForm.result_time && calcClose(editForm.result_time) && (
                      <p className="text-[10px] mt-1" style={{ color: 'var(--neon-accent)' }}>Closing: {calcClose(editForm.result_time)}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Timer (min)</Label>
                    <Input className="neon-input text-sm" type="number" value={editForm.timer_minutes} onChange={e => setEditForm(f => ({ ...f, timer_minutes: parseInt(e.target.value) || 60 }))} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Bet Open Time</Label>
                    <input type="time" className="w-full p-2 rounded-lg text-sm"
                      style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
                      value={editForm.bet_open_time || ''} onChange={e => setEditForm(f => ({ ...f, bet_open_time: e.target.value }))} />
                  </div>
                  <button onClick={() => saveEdit(c.id)} className="btn-neon-primary text-xs col-span-2">Save</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ===== Sub-Admin Results =====
function SAResults() {
  const [companies, setCompanies] = useState([]);
  const [results, setResults] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [winNum, setWinNum] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const fetchData = useCallback(() => {
    api.get('/subadmin/companies').then(r => setCompanies(r.data.companies || [])).catch(() => {});
    api.get('/subadmin/results').then(r => setResults(r.data.results || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const declareResult = async () => {
    if (!selCompany || winNum === '') { toast.error('Company and winning number required'); return; }
    const comp = companies.find(c => c.id === selCompany);
    try {
      await api.post('/subadmin/results', {
        company_id: selCompany,
        company_name: comp?.name || '',
        winning_number: parseInt(winNum),
        winning_option: 'auto',
        date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })()
      });
      toast.success('Result declared! Preview and confirm.');
      setWinNum(''); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const showPreview = async (rid) => {
    try {
      const r = await api.get(`/subadmin/results/${rid}/preview`);
      setPreview(r.data); setPreviewId(rid);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const confirmResult = async (rid) => {
    try {
      const r = await api.put(`/subadmin/results/${rid}/confirm`);
      toast.success(r.data.message);
      setPreview(null); setPreviewId(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4">
      <SectionCard title="Declare Result" icon={Trophy}>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <select data-testid="sa-result-company" value={selCompany} onChange={e => setSelCompany(e.target.value)}
            className="col-span-1 p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <option value="">Select Company</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Input data-testid="sa-result-number" className="neon-input" type="number" placeholder="Winning Number" value={winNum} onChange={e => setWinNum(e.target.value)} />
          <button data-testid="sa-declare-result-btn" onClick={declareResult} className="btn-neon-primary text-sm">Declare</button>
        </div>
      </SectionCard>

      {/* Results List */}
      <SectionCard title="Results History" icon={History}>
        {results.length === 0 ? (
          <p className="text-sm text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>No results yet</p>
        ) : (
          <div className="space-y-2">
            {results.map(r => (
              <div key={r.id} className="glass-card rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{r.company_name}</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{r.date} | Number: <span className="font-bold" style={{ color: 'var(--neon-accent)' }}>{r.winning_number}</span></p>
                  {r.confirmed && <p className="text-[10px]" style={{ color: '#01FFC3' }}>Confirmed | Winners: {r.winner_count || 0} | Payout: {r.total_payout || 0}</p>}
                </div>
                {!r.confirmed && (
                  <div className="flex gap-2">
                    <button data-testid={`sa-preview-${r.id}`} onClick={() => showPreview(r.id)}
                      className="text-xs px-3 py-1.5 rounded-lg font-bold" style={{ background: 'var(--neon-surface)', color: 'var(--neon-accent)' }}>Preview</button>
                    <button data-testid={`sa-confirm-${r.id}`} onClick={() => confirmResult(r.id)}
                      className="text-xs px-3 py-1.5 rounded-lg font-bold" style={{ background: '#01FFC3', color: '#000' }}>Confirm</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Preview Dialog */}
      {preview && (
        <Dialog open={!!preview} onOpenChange={() => { setPreview(null); setPreviewId(null); }}>
          <DialogContent style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.1)', maxHeight: '80vh', overflow: 'auto' }}>
            <DialogHeader><DialogTitle style={{ color: 'var(--neon-primary)' }}>Result Preview - #{preview.winning_number}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Bets Amount</p>
                  <p className="font-black text-lg" style={{ color: 'var(--neon-accent)' }}>{preview.total_bets_amount}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Payout</p>
                  <p className="font-black text-lg" style={{ color: '#FF2A6D' }}>{preview.total_payout}</p>
                </div>
              </div>
              <p className="text-xs font-bold" style={{ color: preview.total_bets_amount - preview.total_payout >= 0 ? '#01FFC3' : '#FF2A6D' }}>
                Profit/Loss: {preview.total_bets_amount - preview.total_payout}
              </p>
              {preview.matches.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-bold" style={{ color: 'var(--neon-text-muted)' }}>Winners ({preview.matches.length}):</p>
                  {preview.matches.map((m, i) => (
                    <div key={i} className="flex justify-between text-xs p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                      <span style={{ color: 'var(--neon-text)' }}>{m.user_name} | {m.option} #{m.number}</span>
                      <span style={{ color: '#01FFC3' }}>+{m.winnings}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => confirmResult(previewId)} className="btn-neon-primary w-full text-sm mt-2">Confirm Result</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ===== Sub-Admin Earnings & Bet Analytics =====
function SAEarnings() {
  const [analytics, setAnalytics] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [analyticsDate, setAnalyticsDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; });

  const fetchAnalytics = useCallback((cid, date) => {
    let url = `/subadmin/bet-analytics?date=${date || analyticsDate}`;
    if (cid) url += `&company_id=${cid}`;
    api.get(url).then(r => setAnalytics(r.data)).catch(() => {});
  }, [analyticsDate]);

  useEffect(() => {
    fetchAnalytics('', analyticsDate);
    api.get('/subadmin/earnings').then(r => setEarnings(r.data)).catch(() => {});
    api.get('/subadmin/companies').then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, [analyticsDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      {/* Earnings Summary */}
      {analytics && (
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-card rounded-xl p-4 text-center">
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Today Earnings</p>
            <p className="text-2xl font-black" style={{ fontFamily: 'var(--font-heading)', color: analytics.today_earnings >= 0 ? '#01FFC3' : '#FF2A6D' }}>{analytics.today_earnings}</p>
          </div>
          <div className="glass-card rounded-xl p-4 text-center">
            <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Earnings</p>
            <p className="text-2xl font-black" style={{ fontFamily: 'var(--font-heading)', color: analytics.total_earnings >= 0 ? '#01FFC3' : '#FF2A6D' }}>{analytics.total_earnings}</p>
          </div>
        </div>
      )}

      {/* Company-wise Bet Analytics */}
      <SectionCard title="Company-wise Bets" icon={BarChart3}>
        {analytics?.by_company?.length > 0 ? (
          <div className="space-y-2">
            {analytics.by_company.map((c, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                <span className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{c.company}</span>
                <div className="flex gap-4 text-xs">
                  <span style={{ color: 'var(--neon-text-secondary)' }}>{c.count} bets</span>
                  <span style={{ color: 'var(--neon-primary)' }}>{c.total} coins</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-center py-3" style={{ color: 'var(--neon-text-muted)' }}>No bets yet</p>}
      </SectionCard>

      {/* Number-wise Top Numbers */}
      <SectionCard title="Top Numbers (Date-wise)" icon={BarChart3}>
        <div className="flex gap-2 mb-3 flex-wrap">
          <input data-testid="sa-analytics-date" type="date" value={analyticsDate}
            onChange={(e) => setAnalyticsDate(e.target.value)}
            className="flex-1 p-2 rounded-lg text-sm"
            style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
          <select data-testid="sa-analytics-company-filter" value={selCompany}
            onChange={e => { setSelCompany(e.target.value); fetchAnalytics(e.target.value, analyticsDate); }}
            className="flex-1 p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {analytics?.date && <p className="text-xs mb-2" style={{ color: 'var(--neon-accent)' }}>Bets for: {analytics.date}</p>}
        {analytics?.top_numbers?.length > 0 ? (
          <div className="space-y-2">
            {analytics.top_numbers.map((n, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                <span className="font-bold" style={{ color: 'var(--neon-accent)' }}>#{n.number}</span>
                <div className="flex gap-4 text-xs">
                  <span style={{ color: 'var(--neon-text-secondary)' }}>{n.count} bets</span>
                  <span style={{ color: 'var(--neon-primary)' }}>{n.total} coins</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-center py-3" style={{ color: 'var(--neon-text-muted)' }}>No bets found</p>}
      </SectionCard>

      {/* Earnings History */}
      <SectionCard title="Earnings History" icon={IndianRupee}>
        {earnings?.logs?.length > 0 ? (
          <div className="space-y-2">
            {earnings.logs.map((l, i) => (
              <div key={i} className="glass-card rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{l.company_name}</p>
                    <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{l.date} | Bets: {l.total_bets} | Payouts: {l.total_winnings}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-sm" style={{ color: l.commission_amount >= 0 ? '#01FFC3' : '#FF2A6D' }}>
                      {l.commission_amount >= 0 ? '+' : ''}{l.commission_amount}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>{l.commission_percent}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-center py-3" style={{ color: 'var(--neon-text-muted)' }}>No earnings yet</p>}
      </SectionCard>
    </div>
  );
}

// ===== MAIN SUB-ADMIN PAGE =====
export default function SubAdminPage() {
  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-black mb-6" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
          Sub-Admin Panel
        </h1>
        <Tabs defaultValue="dashboard">
          <TabsList className="flex flex-wrap gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--neon-elevated)' }}>
            {[
              { v: 'dashboard', icon: LayoutDashboard, l: 'Dashboard' },
              { v: 'users', icon: Users, l: 'Users' },
              { v: 'wallet-requests', icon: Wallet, l: 'Requests' },
              { v: 'companies', icon: Building2, l: 'Companies' },
              { v: 'results', icon: Trophy, l: 'Results' },
              { v: 'bets', icon: History, l: 'Bets' },
              { v: 'earnings', icon: IndianRupee, l: 'Earnings' },
              { v: 'settings', icon: Zap, l: 'Settings' },
            ].map(t => (
              <TabsTrigger key={t.v} data-testid={`sa-tab-${t.v}`} value={t.v}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg data-[state=active]:text-black"
                style={{ fontFamily: 'var(--font-heading)' }}>
                <t.icon size={11} /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard"><SADashboard /></TabsContent>
          <TabsContent value="users"><SAUsers /></TabsContent>
          <TabsContent value="wallet-requests"><SAWalletRequests /></TabsContent>
          <TabsContent value="companies"><SACompanies /></TabsContent>
          <TabsContent value="results"><SAResults /></TabsContent>
          <TabsContent value="bets"><SABets /></TabsContent>
          <TabsContent value="earnings"><SAEarnings /></TabsContent>
          <TabsContent value="settings"><SAProfile /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
